import { ChevronRightIcon } from "lucide-react"
import { type ComponentProps, type ReactNode, useState } from "react"
import { Button } from "~/components/ui/button"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from "~/components/ui/collapsible"
import { cn } from "~/lib/utils"

export function SectionHeader({
	action,
	count,
	icon,
	iconClassName,
	itemName = "item",
	title,
	variant = "default",
	sticky = true,
	toggle
}: {
	toggle?: ReactNode
	action?: ReactNode
	count: number
	icon?: ReactNode
	iconClassName?: string
	itemName?: string
	title: ReactNode
	variant?: "default" | "solid"
	sticky?: boolean
}) {
	return (
		<div
			className={cn(
				"px-4 py-2.5",
				sticky && "sticky top-0 z-10",
				variant === "solid"
					? "bg-background-tertiary"
					: "bg-background/90 shadow-[inset_0_-1px_0_rgba(255,255,255,0.05)] backdrop-blur supports-[backdrop-filter]:bg-background/75"
			)}
		>
			<div className="flex min-w-0 items-center gap-2">
				{toggle}
				{icon && (
					<div
						className={cn(
							"flex size-7 shrink-0 items-center justify-center rounded-md bg-background-secondary text-icons",
							iconClassName
						)}
					>
						{icon}
					</div>
				)}
				<div className="min-w-0">
					<div className="flex min-w-0 items-baseline gap-2">
						<span className="truncate text-sm font-semibold text-text-main">
							{title}
						</span>
						<span className="shrink-0 text-xs font-medium text-text-sub">
							{count} {itemName}
							{count === 1 ? "" : "s"}
						</span>
					</div>
				</div>
				{action && (
					<div className="ml-auto flex shrink-0 items-center gap-2">
						{action}
					</div>
				)}
			</div>
		</div>
	)
}

export function ListSection({
	children,
	collapsible = false,
	variant = "default",
	headerVariant = "default",
	...props
}: Omit<ComponentProps<typeof SectionHeader>, "variant" | "sticky"> & {
	children: ReactNode
	collapsible?: boolean
	variant?: "default" | "raised" | "plain"
	headerVariant?: "default" | "solid"
}) {
	const [open, setOpen] = useState(true)
	const className = cn(
		"min-w-0 overflow-hidden rounded-xl",
		variant === "raised"
			? "surface-raised bg-background-secondary"
			: variant === "plain"
				? "border border-stroke bg-background"
				: "border border-stroke/50 bg-background-secondary/40"
	)
	return collapsible ? (
		<Collapsible open={open} onOpenChange={setOpen} asChild>
			<section className={className}>
				<SectionHeader
					{...props}
					sticky={false}
					variant={headerVariant}
					toggle={
						<CollapsibleTrigger asChild>
							<Button
								variant="ghost"
								size="icon-xs"
								aria-label={`${open ? "Collapse" : "Expand"} ${typeof props.title === "string" ? props.title : "section"}`}
							>
								<ChevronRightIcon
									className={cn(
										"size-3.5 transition-transform motion-reduce:transition-none",
										open && "rotate-90"
									)}
								/>
							</Button>
						</CollapsibleTrigger>
					}
				/>
				<CollapsibleContent>{children}</CollapsibleContent>
			</section>
		</Collapsible>
	) : (
		<section className={className}>
			<SectionHeader {...props} variant={headerVariant} />
			{children}
		</section>
	)
}
